import React, { useRef, useState } from 'react';
import { Upload, Trash2, Loader2, PenLine, Eraser, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { uploadAsset } from '../lib/assets';
import { toast } from 'sonner';

export const ImageUploadField = ({ label, hint, kind, value, onChange, testid, boxClass = 'h-24' }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be smaller than 5MB'); return; }
    setBusy(true);
    try {
      const url = await uploadAsset(file, kind);
      onChange(url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testid}>
      <Label>{label}</Label>
      <div className={`mt-1.5 flex items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3`}>
        <div className={`flex ${boxClass} w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white`}>
          {value ? (
            <img src={value} alt={label} className="max-h-full max-w-full object-contain" crossOrigin="anonymous" />
          ) : (
            <span className="px-2 text-center text-[10px] text-slate-400">No image</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          {hint && <p className="mb-2 text-xs text-slate-500">{hint}</p>}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${testid}-upload-btn`}>
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
              {value ? 'Replace' : 'Upload'}
            </Button>
            {value && (
              <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => onChange('')} data-testid={`${testid}-remove-btn`}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove
              </Button>
            )}
          </div>
        </div>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden" onChange={pick} />
      </div>
    </div>
  );
};

export const SignatureField = ({ label = 'Signature', value, onChange, testid = 'signature-field' }) => {
  const [drawOpen, setDrawOpen] = useState(false);
  return (
    <div>
      <ImageUploadField label={label} kind="signature" value={value} onChange={onChange} testid={testid}
        hint="Upload a scanned signature (PNG with transparent background works best) or draw one." boxClass="h-20" />
      <Button size="sm" variant="outline" className="mt-2" onClick={() => setDrawOpen(true)} data-testid={`${testid}-draw-btn`}>
        <PenLine className="mr-1.5 h-3.5 w-3.5" /> Draw signature
      </Button>
      <SignaturePadDialog open={drawOpen} onOpenChange={setDrawOpen} onSaved={onChange} />
    </div>
  );
};

export const BrandingOverrideSection = ({ form, setField, company = {}, testid = 'doc-branding-section' }) => (
  <details className="rounded-xl border border-slate-200 p-4" data-testid={testid}>
    <summary className="cursor-pointer text-sm font-semibold text-slate-700">Logo, company chop &amp; signature (optional)</summary>
    <p className="mt-2 text-xs text-slate-500">Leave blank to use the defaults from Company Settings, or upload something specific to this document.</p>
    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <ImageUploadField label="Company Logo" kind="logo" value={form.logoUrl || ''} onChange={(v) => setField('logoUrl', v)} testid="doc-logo-upload" />
      <ImageUploadField label="Official Company Chop" kind="chop" value={form.chopUrl || ''} onChange={(v) => setField('chopUrl', v)} testid="doc-chop-upload" />
      <div className="sm:col-span-2"><SignatureField value={form.signatureUrl || ''} onChange={(v) => setField('signatureUrl', v)} testid="doc-signature-upload" /></div>
      <div><Label>Signed By</Label><Input className="mt-1.5" placeholder={company.signedBy || 'Name'} value={form.signedBy || ''} onChange={(e) => setField('signedBy', e.target.value)} data-testid="doc-signed-by" /></div>
      <div><Label>Designation</Label><Input className="mt-1.5" placeholder={company.signedByTitle || 'Designation'} value={form.signedByTitle || ''} onChange={(e) => setField('signedByTitle', e.target.value)} data-testid="doc-signed-title" /></div>
    </div>
  </details>
);

// Editable reference / document number with a "clear" action
export const ReferenceNumberField = ({ value, onChange, testid = 'doc-number-input', label = 'Reference No.' }) => (
  <div>
    <div className="flex items-center justify-between">
      <Label>{label}</Label>
      {value ? (
        <button type="button" onClick={() => onChange('')} className="text-xs font-medium text-red-600 hover:underline" data-testid={`${testid}-clear`}>Clear</button>
      ) : null}
    </div>
    <Input className="mt-1.5" value={value || ''} onChange={(e) => onChange(e.target.value)}
      placeholder="e.g. INV-2026-0001 (leave blank for none)" data-testid={testid} />
  </div>
);

export const SignaturePadDialog = ({ open, onOpenChange, onSaved }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const [busy, setBusy] = useState(false);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#0f172a';
    const p = pos(e);
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
    drawing.current = true; dirty.current = true;
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.lineTo(p.x, p.y); ctx.stroke();
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    dirty.current = false;
  };

  const save = async () => {
    if (!dirty.current) { toast.error('Please draw your signature first'); return; }
    setBusy(true);
    try {
      const blob = await new Promise((res) => canvasRef.current.toBlob(res, 'image/png'));
      const url = await uploadAsset(blob, 'signature', 'signature.png');
      onSaved(url);
      toast.success('Signature saved');
      clear();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save signature');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-lg" data-testid="signature-pad-dialog">
        <DialogHeader><DialogTitle>Draw your signature</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Sign inside the box using your mouse, trackpad or finger.</p>
        <canvas
          ref={canvasRef} width={640} height={220} data-testid="signature-canvas"
          className="w-full cursor-crosshair touch-none rounded-xl border-2 border-dashed border-slate-300 bg-white"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        <DialogFooter>
          <Button variant="outline" onClick={clear} data-testid="signature-clear-btn"><Eraser className="mr-1.5 h-4 w-4" /> Clear</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={busy} onClick={save} data-testid="signature-save-btn">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />} Use signature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
